// Web Speech API wrapper: Text-to-Speech (synthesis) + name recognition
// Everything runs locally in the browser. No audio leaves the device.

export const VOICES = [
  { id: 'boy_1', label: 'Pedrinho', gender: 'boy', pitch: 0.8, rate: 0.95, preferIdx: 0 },
  { id: 'boy_2', label: 'Léo', gender: 'boy', pitch: 0.65, rate: 1.05, preferIdx: 1 },
  { id: 'boy_3', label: 'Gabriel', gender: 'boy', pitch: 0.55, rate: 0.9, preferIdx: 2 },
  { id: 'girl_1', label: 'Aninha', gender: 'girl', pitch: 1.6, rate: 0.95, preferIdx: 0 },
  { id: 'girl_2', label: 'Sofia', gender: 'girl', pitch: 1.4, rate: 1.0, preferIdx: 1 },
  { id: 'girl_3', label: 'Bia', gender: 'girl', pitch: 1.9, rate: 0.9, preferIdx: 2 },
];

export function getVoiceById(id) {
  return VOICES.find((v) => v.id === id) || VOICES[4];
}

let cachedVoices = [];
function refreshVoices() {
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    cachedVoices = window.speechSynthesis.getVoices() || [];
  }
}
if (typeof window !== 'undefined' && window.speechSynthesis) {
  refreshVoices();
  window.speechSynthesis.onvoiceschanged = refreshVoices;
}

export function ttsSupported() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

function pickSystemVoice(profile) {
  const pt = cachedVoices.filter((v) => /pt/i.test(v.lang));
  const list = pt.length ? pt : cachedVoices;
  if (!list.length) return null;
  // Try to match gender hints in the voice name
  const genderHints =
    profile.gender === 'girl'
      ? /(female|mulher|feminin|maria|luciana|google.*brasil|helena|ines)/i
      : /(male|homem|masculin|daniel|felipe|ricardo|joao)/i;
  const matched = list.filter((v) => genderHints.test(v.name));
  const target = matched.length ? matched : list;
  return target[profile.preferIdx % target.length];
}

// Speak text with the chosen voice profile. Returns a promise resolved on end.
export function speak(text, { voiceId = 'girl_2', volume = 1, rate, onEnd, onStart } = {}) {
  return new Promise((resolve) => {
    if (!ttsSupported()) {
      onEnd && onEnd();
      resolve();
      return;
    }
    const synth = window.speechSynthesis;
    synth.cancel();
    if (!cachedVoices.length) refreshVoices();
    const profile = getVoiceById(voiceId);
    const u = new SpeechSynthesisUtterance(text);
    u.lang = 'pt-BR';
    u.pitch = profile.pitch;
    u.rate = rate != null ? rate : profile.rate;
    u.volume = volume;
    const sysVoice = pickSystemVoice(profile);
    if (sysVoice) u.voice = sysVoice;
    u.onstart = () => onStart && onStart();
    u.onend = () => {
      onEnd && onEnd();
      resolve();
    };
    u.onerror = () => {
      onEnd && onEnd();
      resolve();
    };
    synth.speak(u);
  });
}

export function stopSpeaking() {
  if (ttsSupported()) window.speechSynthesis.cancel();
}

// Prime the speech engine from within a user gesture so mobile browsers
// (iOS Safari) allow subsequent programmatic speech. Speaks a silent blip.
export function unlockSpeech() {
  if (!ttsSupported()) return;
  try {
    const synth = window.speechSynthesis;
    if (synth.paused) synth.resume();
    const u = new SpeechSynthesisUtterance(' ');
    u.volume = 0;
    u.lang = 'pt-BR';
    synth.speak(u);
  } catch (err) {
    console.debug('unlockSpeech falhou (ignorado):', err);
  }
}

// ---------- Speech Recognition (name capture) ----------

export function recognitionSupported() {
  return (
    typeof window !== 'undefined' &&
    (window.SpeechRecognition || window.webkitSpeechRecognition)
  );
}

// Listen once and return recognized text. onResult(text), onError(code)
export function listenOnce({ onResult, onError, onEnd } = {}) {
  const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (!SR) {
    onError && onError('unsupported');
    return null;
  }
  const rec = new SR();
  rec.lang = 'pt-BR';
  rec.interimResults = false;
  rec.maxAlternatives = 1;
  rec.continuous = false;
  rec.onresult = (e) => {
    const text = e.results[0][0].transcript;
    onResult && onResult(text);
  };
  rec.onerror = (e) => onError && onError(e.error);
  rec.onend = () => onEnd && onEnd();
  try {
    rec.start();
  } catch (err) {
    onError && onError('start-failed');
  }
  return rec;
}

// Extract a clean first name from a spoken phrase (e.g. "meu nome é Ana" -> "Ana")
export function extractName(transcript) {
  if (!transcript) return '';
  let t = transcript.trim();
  t = t.replace(/^(meu nome (é|e)|me chamo|eu sou (a|o)?|sou (a|o)?|é|e)\s+/i, '');
  const first = t.split(/\s+/)[0] || t;
  const clean = first.replace(/[^\p{L}]/gu, '');
  if (!clean) return '';
  return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
}
